import axios from "axios";
import { BASE_API_URL } from "../config/runtime";
import {
  AUTH_SESSION_RESTORED_EVENT,
  authTokenService,
} from "./authTokenService";
import {
  getNormalizedErrorMessage,
  isDevelopment,
  isPublicAuthRequest,
  isTokenExpiringSoon,
  normalizeUrl,
} from "./axios-helpers";

const api = axios.create({
  baseURL: normalizeUrl(BASE_API_URL),
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

let refreshTokenPromise: Promise<string> | null = null;
let sessionExpiredHandled = false;

window.addEventListener(AUTH_SESSION_RESTORED_EVENT, () => {
  sessionExpiredHandled = false;
});

// Function to clear auth data and redirect to login
function clearAuthAndRedirect() {
  if (sessionExpiredHandled) return;

  sessionExpiredHandled = true;
  authTokenService.clearAuth();
  authTokenService.redirectToLogin("Your session has expired. Please sign in again.");
}

function refreshAccessToken(): Promise<string> {
  if (!refreshTokenPromise) {
    refreshTokenPromise = authTokenService
      .refreshToken()
      .then((result) => result.access_token)
      .catch((error: unknown) => {
        clearAuthAndRedirect();
        throw error;
      })
      .finally(() => {
        refreshTokenPromise = null;
      });
  }

  return refreshTokenPromise;
}

async function getValidAccessToken(): Promise<string | null> {
  const token = authTokenService.getAccessToken();
  if (!token) {
    return null;
  }

  if (!isTokenExpiringSoon(token)) {
    return token;
  }

  return refreshAccessToken();
}

// Attach access_token to Authorization header for every request if available.
// If the token is expired, refresh it before sending the request.
api.interceptors.request.use(
  async (config) => {
    if (config.url) {
      config.url = normalizeUrl(config.url);
    }

    if (isPublicAuthRequest(config.url)) {
      return config;
    }

    const token = await getValidAccessToken();
    if (token) {
      config.headers.set("Authorization", `Bearer ${token}`);
    }
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const normalizedMessage = getNormalizedErrorMessage(error);

    if (status === 401 && !isPublicAuthRequest(error?.config?.url)) {
      clearAuthAndRedirect();
    }

    if (status && status >= 400 && status < 500) {
      if (normalizedMessage) {
        error.message = normalizedMessage;
      }
      if (isDevelopment()) console.warn(`[API ${status}]`, error.message);
      // Don't resolve 4xx errors as success - let them be handled as errors
      return Promise.reject(error);
    }

    if (!error.response) {
      if (isDevelopment()) console.error("[API Network Error]", error?.message || error);
    } else if (isDevelopment()) {
      console.error(`[API ${status}]`, error.message);
    }

    if (normalizedMessage) {
      error.message = normalizedMessage;
    }

    //Apply show noti and write dev log here later

    return Promise.reject(error);
  },
);

export default api;
